import React, { useState, useEffect, useContext } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Axios from 'axios';

import { NewLoginForm } from './NewLoginDailogForm';
import { BackDropLoad } from './LoadingBackDrop';
import { UserContext } from './userContext';

const key = 'dqweqwe123y97qhduiasd$@^!*#&bjasdja&^&@!#';

const IconBook = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);

/* ─────────── Topic card ─────────── */

function TopicCard({ item }) {
  return (
    <RouterLink
      to={`/eng-info/info-detail/${item.info_data_slug}/${item.info_data_id}`}
      title={item.info_child_cat_ids}
      className="
        group relative flex h-full flex-col overflow-hidden rounded-xl
        border border-border-subtle bg-surface-elevated
        transition-[transform,border-color,box-shadow] duration-300 ease-out-soft
        hover:-translate-y-1 hover:border-amber hover:shadow-amber-glow-strong
      "
    >
      {/* Image */}
      <div className="relative aspect-[16/10] overflow-hidden bg-surface-overlay">
        <img
          src={item.image}
          alt=""
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-out-soft group-hover:scale-[1.07]"
        />
        <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/40 to-transparent transition-opacity duration-500 ease-out-soft group-hover:opacity-60" />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 ease-out-soft group-hover:opacity-100"
          style={{ background: 'radial-gradient(ellipse 80% 50% at 50% 100%, rgba(251,191,36,0.16), transparent 70%)' }}
        />
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col gap-1.5 p-4">
        <h3 className="text-[15px] font-semibold leading-tight tracking-[-0.01em] text-fg-primary line-clamp-2 transition-transform duration-300 ease-out-soft group-hover:-translate-y-0.5">
          {item.info_child_cat_ids}
        </h3>

        <div className="mt-2 flex items-center gap-2">
          <span
            aria-hidden
            className="block h-[2px] w-5 bg-amber transition-[width,background-color] duration-500 ease-out-soft group-hover:w-10 group-hover:bg-amber-light"
          />
          <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber opacity-0 -translate-x-1 transition-[opacity,transform] duration-300 ease-out-soft group-hover:opacity-100 group-hover:translate-x-0">
            Open
          </span>
        </div>
      </div>
    </RouterLink>
  );
}

/* ─────────── Empty state ─────────── */

function EmptyTopics() {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border-subtle bg-surface-paper/40 px-6 py-14 text-center">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full border border-border-subtle bg-surface-overlay/60 text-fg-tertiary">
        <IconBook className="h-6 w-6" />
      </div>
      <h3 className="text-[16px] font-semibold text-fg-primary">No topics published yet</h3>
      <p className="mt-1.5 max-w-[380px] text-[13px] leading-relaxed text-fg-secondary">
        Topics will appear here when they&rsquo;re published.
      </p>
    </div>
  );
}

/* ═══════════════════ EngInfoHome ═══════════════════ */

export const EngInfoHome = () => {
  const [loading, setLoading] = useState(true);
  const [infoGrid, setInfoGrid] = useState([]);
  const { user } = useContext(UserContext);
  const [showLoginForm, setShowLoginForm] = useState(!user);
  const [wait, setWait] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    Axios.get('/new_apis/get-enginfo-grid', { headers: { key } }).then((res) => {
      setInfoGrid(res.data.infogrid || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <>
      <BackDropLoad wait={wait} />
      <NewLoginForm
        strict
        strictTitle="Sign in to view Engineering Info"
        strictMessage="Engineering Info is available to ESAY members. Log in to access the full reference library."
        open={showLoginForm}
        showload={() => setWait(true)}
        hideload={() => setWait(false)}
        closeform={() => setShowLoginForm(false)}
        openloginform={() => setShowLoginForm(true)}
      />

      {/* Header */}
      <div className="mb-6">
        <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.16em] text-amber">All topics</div>
        <h2 className="text-2xl md:text-[26px] font-bold leading-[1.15] tracking-[-0.025em] text-fg-primary">
          Browse by topic
        </h2>
        <p className="mt-1.5 max-w-[560px] text-[13.5px] leading-relaxed text-fg-secondary">
          Comprehensive resources and essential reference material for professionals.
        </p>
      </div>

      {/* Body */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="overflow-hidden rounded-xl border border-border-subtle bg-surface-elevated"
            >
              <div className="aspect-[16/10] animate-pulse bg-surface-overlay/60" />
              <div className="p-4 flex flex-col gap-2">
                <div className="h-3 w-3/4 rounded bg-surface-overlay/60 animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      ) : infoGrid.length === 0 ? (
        <EmptyTopics />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {infoGrid.map((item) => (
            <TopicCard key={item.info_data_id} item={item} />
          ))}
        </div>
      )}
    </>
  );
};
